/* eslint-disable @typescript-eslint/no-explicit-any */
import { babyPortalApi } from 'data/axios';
import {
  TCheckoutSessionResponse,
  TCreateCheckoutSessionPayload,
  TReactivateSubscriptionResponse,
  TSelectedSubscriptionInfo
} from 'typings/subscriptionTypes';
import { ApiResponse } from './HttpRequest';
import { endpoints } from './endpoints';

export interface SubscriptionBuyType {
  id: string;
  status: string;
  client_secret: string;
}

type UpgradPayload = {
  purchase_id: string;
  stripe_payment_method: string;
};

type TrialResponse = {
  trial: boolean;
};

export interface SubScriptionState {
  subscriptionData: SubscriptionType[];
  isLoading: boolean;
  selectedSubscriptionInfo: TSelectedSubscriptionInfo;
}

export interface SupportedFeatures {
  id: string;
  description: string;
}

export interface CardDetails {
  brand: string;
  last4: string;
}

export interface Subscription {
  code: number;
  message: string;
  data: { subscriptions: SubscriptionType[] };
  success: boolean;
}

export interface SubscriptionType {
  id: string;
  user_id: string;
  product_id: string;
  sku_id: string;
  sku_name: string;
  space_name: string;
  space_id: string;
  currency: string;
  data_retention_days: number;
  devices: IDevice[];
  end_date?: string;
  next_bill_amount?: number;
  next_billing_date?: string;
  paid_amount: number;
  pay_date?: string;
  level: number;
  pay_type: string;
  start_date: string;
  subscribe_status: string;
  subscription_cycle: number;
  supported_camera_count: number;
  supported_features: SupportedFeatures[];
  trial: boolean;
  trial_start_date: number;
  cancel_date?: string;
  purchase_id?: string;
  card_details?: CardDetails;
}

export interface IDevice {
  id: string;
  name: string;
}

export async function GetSubscriptionDetails(): Promise<
  ApiResponse<Subscription>
> {
  const response = await babyPortalApi.get(endpoints.subscriptionUrl);
  return response;
}

export async function UpgradeUserSubscription(
  payload: UpgradPayload
): Promise<ApiResponse<SubscriptionBuyType>> {
  const response = await babyPortalApi.post(
    endpoints.subscriptionUpgradeUrl,
    payload
  );
  return response;
}

export async function BuyUserSubscription(
  payload: UpgradPayload
): Promise<ApiResponse<SubscriptionBuyType>> {
  const response = await babyPortalApi.post(
    endpoints.subscriptionBuyUrl,
    payload
  );
  return response;
}

export async function CheckFreeTrialSubscription(): Promise<
  ApiResponse<TrialResponse>
> {
  const response = await babyPortalApi.get(endpoints.checkTrialUrl);
  return response;
}

export async function AddDevicesToSubscription({
  subscriptionId,
  payload
}: {
  subscriptionId: string;
  payload: {
    device_ids: string[];
  };
}) {
  const response = await babyPortalApi.post(
    `${endpoints.subscriptionUrl}/${subscriptionId}/devices`,
    payload
  );
  return response;
}

export async function AddDeviceToSubscription({
  subscriptionId,
  payload
}: {
  subscriptionId: string;
  payload: {
    device_id: string;
  };
}) {
  const response = await babyPortalApi.post(
    `${endpoints.subscriptionUrl}/${subscriptionId}/devices`,
    payload
  );
  return response;
}

export async function RemoveDeviceFromSubscription({
  dId,
  purchaseId
}: {
  purchaseId: string;
  dId: string;
}) {
  const response = await babyPortalApi.delete(
    `${endpoints.subscriptionUrl}/${purchaseId}/devices/${dId}`
  );
  return response;
}

export async function ListAllDevices() {
  const response = await babyPortalApi.get(endpoints.listDevicesUrl);
  return response;
}

export async function UpdateDevice({
  deviceId,
  payload
}: {
  deviceId: string;
  payload: {
    [key: string]: string | boolean | number;
  };
}) {
  const response = await babyPortalApi.patch(
    endpoints.updateDevices(deviceId),
    payload
  );
  return response;
}

export async function ActivateDevice({ deviceId }: { deviceId: string }) {
  const response = await UpdateDevice({
    deviceId,
    payload: {
      is_activated: true
    }
  });
  return response;
}

export async function DeleteSubscription({
  purchaseId
}: {
  purchaseId: string;
}) {
  const url = `${endpoints.subscriptionBuyUrl}/${purchaseId}`;
  const response = await babyPortalApi.delete(url);
  return response;
}

export async function GetProRateCharge({ purchaseId }: { purchaseId: string }) {
  const url = `${endpoints.subscriptionBuyUrl}/${purchaseId}/prorate_charges`;
  const response = await babyPortalApi.get(url);
  return response;
}

export async function createCheckoutSession(
  payload: TCreateCheckoutSessionPayload
): Promise<ApiResponse<TCheckoutSessionResponse>> {
  const response = await babyPortalApi.post(endpoints.checkoutSession, payload);
  return response;
}

export async function reactivateSubscription(
  id: string
): Promise<ApiResponse<TReactivateSubscriptionResponse>> {
  const response = await babyPortalApi.patch(
    endpoints.reactivateSubscription(id)
  );
  return response;
}
